import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabase/config';
import {
    fetchMyBusinesses,
    fetchAllBusinesses,
    autoJoinBusiness,
    updateCustomerProfileAcrossBusinesses,
    recoverCustomerSessions
} from '../../services/globalPortalService';

import GlobalBusinessList from '../../components/portal/GlobalBusinessList';
import GlobalExploreTab from '../../components/portal/GlobalExploreTab';
import GlobalProfileTab from '../../components/portal/GlobalProfileTab';
import GlobalLoginModal from '../../components/portal/GlobalLoginModal';
import GlobalBottomNav from '../../components/portal/GlobalBottomNav';

import { useAlert } from '../../context/AlertContext';

const GlobalCustomerPortal = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();
    const [businesses, setBusinesses] = useState([]);
    const [allBusinesses, setAllBusinesses] = useState([]); // All tenants in the system
    const [activeTab, setActiveTab] = useState('businesses');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [loading, setLoading] = useState(true);

    // OTP States
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginStep, setLoginStep] = useState('phone'); // phone, code
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [formattedPhoneForOtp, setFormattedPhoneForOtp] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);

    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        birthday: ''
    });
    const [updateLoading, setUpdateLoading] = useState(false);

    // Settings & Preferences
    const [businessPrefs, setBusinessPrefs] = useState(() => {
        return JSON.parse(localStorage.getItem('crm_customer_prefs') || '{}');
    });

    useEffect(() => {
        localStorage.setItem('crm_customer_prefs', JSON.stringify(businessPrefs));
    }, [businessPrefs]);

    const primaryColor = '#6f4e37'; // Ugra Coffee Primary

    const fetchBusinesses = async () => {
        setLoading(true);
        const globalId = localStorage.getItem('crm_global_customer_id');

        if (!globalId) {
            // Not logged in — redirect to login
            navigate('/customer-login');
            return;
        }

        const [myBiz, allBiz] = await Promise.all([
            fetchMyBusinesses(globalId),
            fetchAllBusinesses()
        ]);

        // Filter out ugra system tenant from member list
        const memberBusinesses = myBiz
            .filter(b => b.tenant?.slug !== 'ugra')
            .sort((a, b) => {
                const orderA = businessPrefs[a.id]?.order ?? 0;
                const orderB = businessPrefs[b.id]?.order ?? 0;
                return orderA - orderB;
            });

        // For "Yakındakiler", exclude businesses the user is already a member of
        const memberTenantIds = new Set(memberBusinesses.map(b => b.id));
        const explorableBusinesses = allBiz.filter(b => !memberTenantIds.has(b.id));

        setBusinesses(memberBusinesses);
        setAllBusinesses(explorableBusinesses);
        setLoading(false);
    };

    // Auto-join a new business and navigate to its portal
    const autoJoinAndNavigate = async (tenantId, tenantSlug) => {
        const globalId = localStorage.getItem('crm_global_customer_id');
        if (!globalId) {
            navigate('/customer-login');
            return;
        }

        const result = await autoJoinBusiness(globalId, tenantId);
        if (result) {
            // Refresh the business list to include the new one
            await fetchBusinesses();
            // Navigate to the portal
            navigate(`/portal/${tenantSlug || tenantId}`);
        } else {
            showAlert('Bir hata oluştu. Lütfen tekrar deneyin.', 'error');
        }
    };

    const toggleNotifications = (tId) => {
        setBusinessPrefs(prev => ({
            ...prev,
            [tId]: {
                ...prev[tId],
                notificationsDisabled: !prev[tId]?.notificationsDisabled
            }
        }));
    };

    const toggleFavorite = (tId) => {
        setBusinessPrefs(prev => ({
            ...prev,
            [tId]: {
                ...prev[tId],
                favorite: !prev[tId]?.favorite
            }
        }));
    };

    const moveBusiness = (index, direction) => {
        const newBusinesses = [...businesses];
        const targetIndex = index + direction;
        if (targetIndex >= 0 && targetIndex < newBusinesses.length) {
            [newBusinesses[index], newBusinesses[targetIndex]] = [newBusinesses[targetIndex], newBusinesses[index]];
            setBusinesses(newBusinesses);

            // Save order to prefs
            const newPrefs = { ...businessPrefs };
            newBusinesses.forEach((biz, idx) => {
                newPrefs[biz.id] = { ...newPrefs[biz.id], order: idx };
            });
            setBusinessPrefs(newPrefs);
        }
    };

    useEffect(() => {
        fetchBusinesses();
    }, []);

    useEffect(() => {
        const loadProfile = async () => {
            const globalId = localStorage.getItem('crm_global_customer_id');

            // 1. En güvenilir yöntem: Doğrudan Global ID ile Müşteri Çekme
            if (globalId) {
                const { data: globalRec } = await supabase
                    .from('customers')
                    .select('*')
                    .eq('id', globalId)
                    .single();

                if (globalRec) {
                    setProfileData({
                        firstName: globalRec.first_name || '',
                        lastName: globalRec.last_name || '',
                        phone: globalRec.phone || '',
                        email: globalRec.email || '',
                        birthday: globalRec.birthday || ''
                    });
                    return; // Profil bulundu, işletmelere bakmaya gerek yok
                }
            }

            // 2. Fallback (Geriye Dönük Uyumluluk): İşletmelerden Çekme
            const firstBiz = businesses.find(b => b.customer);
            if (firstBiz) {
                const c = firstBiz.customer;
                let fname = c.firstName || '';
                let lname = c.lastName || '';

                if (!fname && !lname && c.name) {
                    const parts = c.name.trim().split(' ');
                    fname = parts[0] || '';
                    lname = parts.slice(1).join(' ') || '';
                }

                setProfileData({
                    firstName: fname,
                    lastName: lname,
                    phone: c.phone || '',
                    email: c.email || '',
                    birthday: c.birthday || ''
                });
            }
        };
        loadProfile();
    }, [businesses]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        try {
            const globalId = localStorage.getItem('crm_global_customer_id');
            await updateCustomerProfileAcrossBusinesses(globalId, profileData);

            // Refresh data
            await fetchBusinesses();
            showAlert("Profil bilgileriniz tüm işletmelerde güncellendi!", "success");
        } catch (error) {
            console.error("Error updating profile:", error);
            showAlert("Profil güncellenirken bir hata oluştu.", "error");
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleGlobalLogout = async () => {
        const confirmed = await showConfirm(
            t('common.confirmSignOut', 'Oturumunuzu kapatmak istediğinize emin misiniz?'),
            { title: 'Çıkış Yap', confirmText: 'Çıkış Yap', type: 'warning' }
        );
        if (!confirmed) return;

        localStorage.removeItem('crm_global_customer_id');
        localStorage.removeItem('crm_customer_prefs');

        navigate('/customer-login');
    };

    const handleBusinessExit = async (tId, tName) => {
        const confirmed = await showConfirm(
            `${tName} işletmesine gittiğinizde puanlarınız yine orada görünecek. Devam etmek istiyor musunuz?`,
            { title: 'İşletmeden Ayrıl', confirmText: 'Devam Et', type: 'info' }
        );
        if (!confirmed) return;
        setBusinessPrefs(prev => ({ ...prev, [tId]: { ...prev[tId], hidden: true } }));
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError(null);

        // Format phone: if no +, add +90
        let formattedPhone = phoneNumber.trim();
        if (!formattedPhone.startsWith('+')) {
            formattedPhone = `+90${formattedPhone.replace(/^0/, '')}`;
        }

        try {
            const { error } = await supabase.auth.signInWithOtp({ phone: formattedPhone });
            if (error) throw error;
            setFormattedPhoneForOtp(formattedPhone);
            setLoginStep('code');
        } catch (err) {
            console.error("OTP Send Error:", err);
            setAuthError("SMS gönderilemedi. Lütfen numarayı kontrol edip tekrar deneyin.");
        } finally {
            setAuthLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError(null);

        try {
            const { data, error } = await supabase.auth.verifyOtp({
                phone: formattedPhoneForOtp,
                token: verificationCode,
                type: 'sms'
            });
            if (error) throw error;

            const verifiedPhone = data?.user?.phone || formattedPhoneForOtp;
            const recoveredId = await recoverCustomerSessions(phoneNumber, verifiedPhone);

            if (!recoveredId) {
                setAuthError("Bu numara ile kayıtlı bir puan bulunamadı.");
            } else {
                await fetchBusinesses();
                setShowLoginModal(false);
                showAlert('Hesabınız başarıyla kurtarıldı!', 'success');
            }
        } catch (err) {
            console.error("OTP Verify Error:", err);
            setAuthError("Hatalı kod. Lütfen tekrar deneyin.");
        } finally {
            setAuthLoading(false);
        }
    };

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-main)' }}>
            <div className="spinner" style={{ borderTopColor: 'var(--primary)' }}></div>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f4f4f6', paddingBottom: '7rem', color: 'var(--text-main)' }}>
            {/* Mesh Background Effect */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                overflow: 'hidden',
                pointerEvents: 'none'
            }}>
                <div style={{
                    position: 'absolute',
                    top: '-10%',
                    right: '-10%',
                    width: '60vw',
                    height: '60vw',
                    background: `radial-gradient(circle, ${primaryColor}15 0%, transparent 70%)`,
                    borderRadius: '50%'
                }}></div>
                <div style={{
                    position: 'absolute',
                    bottom: '-10%',
                    left: '-10%',
                    width: '50vw',
                    height: '50vw',
                    background: `radial-gradient(circle, ${primaryColor}10 0%, transparent 70%)`,
                    borderRadius: '50%'
                }}></div>
            </div>

            {/* Header */}
            <div style={{
                background: `linear-gradient(150deg, #3d2211 0%, ${primaryColor} 55%, #9a7060 100%)`,
                color: 'white',
                padding: '3rem 1.5rem 1.75rem',
                position: 'relative',
                overflow: 'hidden',
                zIndex: 1
            }}>
                {/* Decorative circles */}
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }}></div>
                <div style={{ position: 'absolute', bottom: '-40px', left: '20px', width: '170px', height: '170px', borderRadius: '50%', background: 'rgba(0,0,0,0.08)', pointerEvents: 'none' }}></div>

                {/* Top bar: Logo + Profile Avatar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: activeTab === 'businesses' ? '1.5rem' : '0.875rem', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src="/favicon.svg" alt="Logo" style={{ width: '34px', height: '34px', borderRadius: '10px', objectFit: 'cover' }} />
                        <span style={{ fontWeight: 600, fontSize: '1.5rem', color: 'white', fontFamily: 'var(--font-logo)', position: 'relative', top: '2px' }}>ugra</span>
                    </div>
                    <div
                        onClick={() => setActiveTab('profile')}
                        style={{
                            width: '42px', height: '42px', borderRadius: '50%',
                            background: 'rgba(255,255,255,0.18)',
                            border: '2px solid rgba(255,255,255,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '1rem', fontWeight: 900, cursor: 'pointer',
                            backdropFilter: 'blur(4px)', color: 'white', flexShrink: 0
                        }}
                    >
                        {profileData.firstName?.[0]?.toUpperCase() || '?'}
                    </div>
                </div>

                {/* Businesses tab: greeting + stats */}
                {activeTab === 'businesses' ? (
                    <div style={{ position: 'relative' }}>
                        <p style={{ fontSize: '0.8rem', opacity: 0.75, marginBottom: '4px', fontWeight: 500, color: 'white' }}>Hoş geldiniz,</p>
                        <h1 style={{ fontSize: '2rem', fontWeight: 900, marginBottom: '1.25rem', letterSpacing: '-1px', color: 'white', lineHeight: 1.1 }}>
                            {profileData.firstName || 'Müşteri'}
                        </h1>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{
                                flex: 1, background: 'rgba(255,255,255,0.13)',
                                borderRadius: '16px', padding: '12px 14px',
                                border: '1px solid rgba(255,255,255,0.18)',
                                backdropFilter: 'blur(8px)'
                            }}>
                                <div style={{ fontSize: '1.625rem', fontWeight: 900, lineHeight: 1, color: 'white' }}>
                                    {businesses.filter(b => !businessPrefs[b.id]?.hidden).length}
                                </div>
                                <div style={{ fontSize: '0.6875rem', opacity: 0.8, marginTop: '5px', fontWeight: 700, letterSpacing: '0.4px', color: 'white' }}>İŞLETME</div>
                            </div>
                            <div style={{
                                flex: 1, background: 'rgba(255,255,255,0.13)',
                                borderRadius: '16px', padding: '12px 14px',
                                border: '1px solid rgba(255,255,255,0.18)',
                                backdropFilter: 'blur(8px)'
                            }}>
                                <div style={{ fontSize: '1.625rem', fontWeight: 900, lineHeight: 1, color: 'white' }}>
                                    {businesses.reduce((sum, b) => sum + (b.customer?.points || 0), 0)}
                                </div>
                                <div style={{ fontSize: '0.6875rem', opacity: 0.8, marginTop: '5px', fontWeight: 700, letterSpacing: '0.4px', color: 'white' }}>TOPLAM PUAN</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ position: 'relative' }}>
                        <h1 style={{ fontSize: '1.625rem', fontWeight: 900, letterSpacing: '-0.5px', color: 'white', lineHeight: 1.15, marginBottom: 0 }}>
                            {activeTab === 'explore' ? 'Kampanyalar' :
                                activeTab === 'profile' ? 'Profilim' : 'Ayarlar'}
                        </h1>
                        <p style={{ fontSize: '0.8125rem', opacity: 0.7, marginTop: '7px', fontWeight: 500, color: 'white' }}>
                            {activeTab === 'explore' ? 'Aktif kampanya ve teklifleri keşfedin' :
                                activeTab === 'profile' ? 'Kişisel bilgilerinizi yönetin' : 'Bildirim ve üyelik ayarları'}
                        </p>
                    </div>
                )}
            </div>

            <div style={{
                maxWidth: '540px',
                margin: '0 auto',
                padding: '1.25rem 1.25rem 2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                position: 'relative',
                zIndex: 10
            }}>
                {/* Filter Navigation */}
                {(activeTab === 'businesses' || activeTab === 'explore') && (
                    <div className="filter-nav-container" style={{ 
                        display: 'flex', 
                        gap: '10px', 
                        overflowX: 'auto', 
                        padding: '4px 0 10px',
                        marginBottom: '0.5rem',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        maskImage: 'linear-gradient(to right, black 85%, transparent 100%)'
                    }}>
                        {[
                            { id: 'all', label: 'Tümü' },
                            { id: 'nearby', label: 'Keşfet' },
                            { id: 'favorites', label: 'Favoriler' }
                        ].map(f => (
                            <button
                                key={f.id}
                                onClick={() => setSelectedFilter(f.id)}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: '14px',
                                    border: '1px solid',
                                    borderColor: selectedFilter === f.id ? primaryColor : 'rgba(0,0,0,0.05)',
                                    background: selectedFilter === f.id ? primaryColor : 'white',
                                    color: selectedFilter === f.id ? 'white' : '#666',
                                    fontSize: '0.8125rem',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: selectedFilter === f.id ? `0 8px 20px -6px ${primaryColor}40` : '0 4px 12px rgba(0,0,0,0.03)',
                                    transform: selectedFilter === f.id ? 'scale(1.02)' : 'scale(1)'
                                }}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                )}

                {(activeTab === 'businesses' || activeTab === 'settings') && (
                    <GlobalBusinessList
                        businesses={(() => {
                            if (activeTab === 'settings') return businesses.filter(b => !businessPrefs[b.id]?.hidden);
                            const visibleBiz = businesses.filter(b => !businessPrefs[b.id]?.hidden);
                            if (selectedFilter === 'favorites') return visibleBiz.filter(b => businessPrefs[b.id]?.favorite);
                            if (selectedFilter === 'nearby') {
                                // Include unjoined businesses + hidden (exited) member businesses
                                const hiddenMembers = businesses.filter(b => businessPrefs[b.id]?.hidden);
                                return [...allBusinesses, ...hiddenMembers];
                            }
                            return visibleBiz; // "Tümü" = all member businesses
                        })()}
                        activeTab={activeTab}
                        businessPrefs={businessPrefs}
                        toggleNotifications={toggleNotifications}
                        moveBusiness={moveBusiness}
                        handleBusinessExit={handleBusinessExit}
                        setShowLoginModal={setShowLoginModal}
                        handleGlobalLogout={handleGlobalLogout}
                        t={t}
                        navigate={navigate}
                        primaryColor={primaryColor}
                        toggleFavorite={toggleFavorite}
                        selectedFilter={selectedFilter}
                        autoJoinAndNavigate={autoJoinAndNavigate}
                        setBusinessPrefs={setBusinessPrefs}
                    />
                )}

                {activeTab === 'explore' && (
                    <GlobalExploreTab
                        businesses={businesses}
                        allBusinesses={allBusinesses}
                        selectedFilter={selectedFilter}
                        businessPrefs={businessPrefs}
                        navigate={navigate}
                    />
                )}

                {activeTab === 'profile' && (
                    <GlobalProfileTab
                        profileData={profileData}
                        setProfileData={setProfileData}
                        handleUpdateProfile={handleUpdateProfile}
                        updateLoading={updateLoading}
                        t={t}
                    />
                )}
            </div>

            <GlobalLoginModal
                showLoginModal={showLoginModal}
                setShowLoginModal={setShowLoginModal}
                loginStep={loginStep}
                setLoginStep={setLoginStep}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                verificationCode={verificationCode}
                setVerificationCode={setVerificationCode}
                handleSendOTP={handleSendOTP}
                handleVerifyOTP={handleVerifyOTP}
                authLoading={authLoading}
                authError={authError}
                t={t}
                primaryColor={primaryColor}
            />

            <GlobalBottomNav
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                t={t}
                primaryColor={primaryColor}
            />

            <style>{`
                .ai-glow-button {
                    position: relative;
                    overflow: hidden;
                }
                .ai-glow-button::after {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent);
                    transform: rotate(45deg);
                    animation: shimmer 3s infinite;
                }
                @keyframes shimmer {
                    0% { transform: translateX(-150%) rotate(45deg); }
                    100% { transform: translateX(150%) rotate(45deg); }
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                .logo-glow {
                    animation: logoPulse 4s infinite ease-in-out;
                }
                @keyframes logoPulse {
                    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                    50% { box-shadow: 0 0 25px 5px rgba(255, 255, 255, 0.5), 0 0 50px 10px rgba(255, 215, 0, 0.3); }
                    100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }
            `}</style>
        </div >
    );
};

export default GlobalCustomerPortal;
